import React, { useState } from 'react';
import { dashboardAPI } from '../api';
import Plotly from 'react-plotly.js';

/**
 * Componente UploadToDatabase
 * Réplica EXACTA del método upload_to_database() de dashboard_ui.py (líneas 3390-3688)
 * 
 * Flujo:
 * 1. Seleccionar marketplace
 * 2. Subir archivo CSV
 * 3. Validar formato y mostrar estadísticas
 * 4. Confirmar carga a base de datos
 */
const UploadToDatabase = ({ marketplaces, onUploadSuccess }) => {
  const [marketplace, setMarketplace] = useState('amazon');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progressPhase, setProgressPhase] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validar que sea CSV
      if (!selectedFile.name.endsWith('.csv')) {
        setError('El archivo debe ser un CSV');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
      setSuccess(null);
      setValidationData(null);
    }
  };

  const handleValidateFile = async () => {
    if (!file) {
      setError('Por favor seleccione un archivo CSV');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setProgressPhase('Cargando y procesando archivo CSV...');
    setProgressPercent(10);

    try {
      // Llamar al endpoint de validación (Fase 1-3 de Streamlit)
      const response = await dashboardAPI.uploadToDatabase.validateCSV(file, marketplace);
      
      setProgressPercent(100);
      setValidationData(response);
      setSuccess(`✅ ${response.message}`);
      
      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error validando archivo:', err);
      
      // Manejar errores de validación detallados
      if (err.response?.data?.detail) {
        try {
          const errorDetail = JSON.parse(err.response.data.detail);
          if (errorDetail.error) {
            setError(
              <div>
                <p><strong>{errorDetail.error}</strong></p>
                <p><strong>Formato requerido:</strong> {errorDetail.required_format}</p>
                <p><strong>Columnas encontradas:</strong> {errorDetail.columns_found?.join(', ') || 'N/A'}</p>
                {errorDetail.columns_missing?.length > 0 && (
                  <p><strong>Columnas faltantes:</strong> {errorDetail.columns_missing.join(', ')}</p>
                )}
                {errorDetail.hist_warning && (
                  <p style={{ color: 'var(--color-accent-orange)' }}>⚠️ {errorDetail.hist_warning}</p>
                )}
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--color-light-gray)', borderRadius: '8px' }}>
                  <strong>Correcciones sugeridas:</strong>
                  <ol style={{ marginTop: '8px', marginBottom: 0 }}>
                    <li>Asegúrese de que el archivo tenga las columnas requeridas</li>
                    <li>Los nombres de columnas deben coincidir exactamente (distingue mayúsculas/minúsculas)</li>
                    <li>Las columnas históricas deben comenzar con 'u_' seguido del año (ej: u_2021)</li>
                  </ol>
                </div>
              </div>
            );
          } else {
            setError(err.response.data.detail);
          }
        } catch {
          setError(err.response?.data?.detail || err.message);
        }
      } else {
        setError(err.message || 'Error desconocido al validar archivo');
      }
    } finally {
      setLoading(false);
      setProgressPhase(null);
      setProgressPercent(0);
    }
  };

  const handleConfirmUpload = async () => {
    if (!validationData || !validationData.upload_id) {
      setError('No hay datos validados para cargar. Por favor valide el archivo primero.');
      return;
    }

    if (uploadInProgress) {
      setError('Una carga ya está en progreso. Por favor espere...');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setUploadInProgress(true);

    try {
      // Fase 1: Preparar conexión
      setProgressPhase('Fase 1/3: Preparando conexión a la base de datos...');
      setProgressPercent(10);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Fase 2: Preparar metadatos
      setProgressPhase('Fase 2/3: Preparando metadatos y datos para carga...');
      setProgressPercent(30);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Fase 3: Cargar a BD
      setProgressPhase('Fase 3/3: Cargando datos a la base de datos (esto puede tomar unos minutos)...');
      setProgressPercent(50);

      const response = await dashboardAPI.uploadToDatabase.confirmUpload(
        validationData.upload_id,
        marketplace
      );

      setProgressPercent(100);
      
      setSuccess(
        <div>
          <p><strong>✅ {response.message}</strong></p>
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--color-accent-green)', color: 'white', borderRadius: '8px' }}>
            <p><strong>Estadísticas de carga:</strong></p>
            <ul style={{ marginBottom: 0 }}>
              <li>Filas cargadas: {response.stats?.rows_loaded?.toLocaleString()}</li>
              <li>SKUs únicos: {response.stats?.unique_skus?.toLocaleString()}</li>
              <li>Total predicho: {response.stats?.total_predicted?.toLocaleString()}</li>
            </ul>
          </div>
        </div>
      );

      // Limpiar estado
      setFile(null);
      setValidationData(null);
      
      // Notificar éxito al componente padre
      if (onUploadSuccess) {
        setTimeout(() => {
          onUploadSuccess();
        }, 2000);
      }

    } catch (err) {
      console.error('Error cargando a base de datos:', err);
      
      let errorMsg = err.response?.data?.detail || err.message || 'Error desconocido al cargar datos';
      
      // Agregar sugerencias específicas para errores comunes
      if (errorMsg.toLowerCase().includes('timeout') || errorMsg.toLowerCase().includes('lock')) {
        errorMsg += '\n\nPosible problema de timeout o bloqueo:\n• Intente con un archivo más pequeño\n• Si el archivo es grande, considere dividirlo en partes más pequeñas\n• Espere unos minutos antes de intentar nuevamente';
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
      setUploadInProgress(false);
      setProgressPhase(null);
      setProgressPercent(0);
    }
  };

  return (
    <div>
      {/* Título y descripción - Réplica de líneas 3399-3407 */}
      <h3 className="title-secondary" style={{ marginTop: 'var(--spacing-lg)', marginBottom: 'var(--spacing-md)' }}>
        Cargar datos a la Base de Datos
      </h3>
      
      <div className="card" style={{ 
        backgroundColor: 'var(--color-light-blue)', 
        padding: 'var(--spacing-md)',
        marginBottom: 'var(--spacing-lg)',
        border: '1px solid var(--color-primary-blue)',
        borderRadius: 'var(--border-radius-lg)'
      }}>
        <p style={{ margin: 0, marginBottom: 'var(--spacing-sm)' }}>
          Esta opción permite subir un archivo CSV con datos de predicción a la base de datos.
        </p>
        <div style={{ marginTop: 'var(--spacing-sm)' }}>
          <p style={{ margin: 0, fontWeight: 'var(--font-weight-semibold)' }}>Formato requerido:</p>
          <ul style={{ marginTop: 'var(--spacing-xs)', marginBottom: 0, paddingLeft: '20px' }}>
            <li>El CSV debe tener las columnas: <code>sku</code>, <code>product_name</code>, <code>month</code>, <code>u_YYYY</code> (años históricos) y <code>pred</code></li>
            <li>La columna 'u_YYYY' representa los años históricos (ej: u_2021, u_2022, u_2023)</li>
            <li>La columna 'pred' contiene los valores de predicción</li>
          </ul>
        </div>
      </div>

      {/* Selección de marketplace - Réplica de líneas 3409-3414 */}
      <div className="form-group" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label className="form-label">
          Marketplace:
        </label>
        <select 
          value={marketplace} 
          onChange={(e) => setMarketplace(e.target.value)}
          className="form-select"
          style={{ width: 'auto', minWidth: '200px' }}
        >
          {marketplaces.map(mp => (
            <option key={mp.id} value={mp.id}>{mp.name}</option>
          ))}
        </select>
      </div>

      {/* Subir archivo CSV - Réplica de líneas 3416-3417 */}
      <div className="form-group" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label className="form-label">
          Subir archivo CSV:
        </label>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="input-field"
          style={{ width: '100%', maxWidth: '500px' }}
        />
        {file && (
          <p style={{ marginTop: 'var(--spacing-xs)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            Archivo seleccionado: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
          </p>
        )}
      </div>

      {/* Botón para validar archivo */}
      {file && !validationData && (
        <button
          onClick={handleValidateFile}
          disabled={loading}
          className={`btn btn-primary ${loading ? 'disabled' : ''}`}
          style={{ marginBottom: 'var(--spacing-lg)' }}
        >
          {loading ? 'Validando archivo...' : 'Validar y procesar archivo'}
        </button>
      )}

      {/* Barra de progreso - Réplica de líneas 3574-3611 */}
      {loading && progressPhase && (
        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div className="card" style={{ 
            backgroundColor: 'var(--color-primary-blue)',
            color: 'white',
            padding: 'var(--spacing-md)'
          }}>
            <p style={{ margin: 0, marginBottom: 'var(--spacing-sm)', fontWeight: 'var(--font-weight-semibold)' }}>
              {progressPhase}
            </p>
            <div style={{ 
              width: '100%', 
              height: '20px', 
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: 'var(--border-radius-sm)',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${progressPercent}%`,
                height: '100%',
                backgroundColor: 'var(--color-accent-green)',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <p style={{ margin: 0, marginTop: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}>
              {progressPercent}% completado
            </p>
          </div>
        </div>
      )}

      {/* Mensajes de error */}
      {error && (
        <div className="message message-error" style={{ marginBottom: 'var(--spacing-lg)' }}>
          {error}
        </div>
      )}

      {/* Mensajes de éxito */}
      {success && (
        <div className="message message-success" style={{ marginBottom: 'var(--spacing-lg)' }}>
          {success}
        </div>
      )}

      {/* Datos de validación - Réplica de líneas 3453-3515 */}
      {validationData && (
        <div>
          {/* Estadísticas básicas - Réplica de líneas 3456-3466 */}
          <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <h4 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-primary-blue)' }}>
              Archivo CSV cargado correctamente: {validationData.filename}
            </h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-md)'
            }}>
              <div className="metric-card">
                <div className="metric-value">{validationData.stats?.total_rows?.toLocaleString()}</div>
                <div className="metric-label">Total filas</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">{validationData.stats?.unique_skus?.toLocaleString()}</div>
                <div className="metric-label">SKUs únicos</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">{validationData.stats?.hist_years_count}</div>
                <div className="metric-label">Años históricos</div>
              </div>
            </div>
            
            {/* Advertencias si existen */}
            {validationData.warnings && validationData.warnings.length > 0 && (
              <div style={{ 
                marginTop: 'var(--spacing-md)',
                padding: 'var(--spacing-md)',
                backgroundColor: 'var(--color-accent-yellow)',
                borderRadius: 'var(--border-radius-sm)',
                border: '1px solid var(--color-accent-orange)'
              }}>
                <strong>⚠️ Advertencias:</strong>
                <ul style={{ marginTop: 'var(--spacing-xs)', marginBottom: 0, paddingLeft: '20px' }}>
                  {validationData.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Vista previa de datos - Réplica de líneas 3494-3515 */}
          <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <h4 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-primary-blue)' }}>
              Vista previa de los datos
            </h4>
            
            {validationData.preview_data && validationData.preview_data.length > 0 && (
              <div className="table-container" style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: 'var(--spacing-md)' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      {validationData.columns?.map((col, index) => (
                        <th key={index}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {validationData.preview_data.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {validationData.columns?.map((col, colIndex) => (
                          <td key={colIndex}>{row[col]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Gráfico de distribución por mes - Réplica de líneas 3498-3515 */}
            {validationData.month_distribution && Object.keys(validationData.month_distribution).length > 0 && (
              <div>
                <h5 style={{ marginBottom: 'var(--spacing-sm)' }}>Distribución por mes</h5>
                <Plotly
                  data={[{
                    x: Object.keys(validationData.month_distribution).map(Number),
                    y: Object.values(validationData.month_distribution),
                    type: 'bar',
                    marker: { color: 'var(--color-primary-blue)' }
                  }]}
                  layout={{
                    xaxis: { title: 'Mes' },
                    yaxis: { title: 'Cantidad de registros' },
                    title: 'Distribución por mes',
                    template: 'plotly_white',
                    height: 300
                  }}
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>

          {/* Confirmación de carga - Réplica de líneas 3550-3666 */}
          <div className="card" style={{ 
            backgroundColor: 'var(--color-light-gray)',
            padding: 'var(--spacing-lg)',
            border: '2px solid var(--color-accent-orange)'
          }}>
            <h4 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-accent-orange)' }}>
              Cargar a la base de datos
            </h4>
            
            <div style={{ 
              marginBottom: 'var(--spacing-md)',
              padding: 'var(--spacing-md)',
              backgroundColor: 'var(--color-accent-yellow)',
              borderRadius: 'var(--border-radius-sm)',
              border: '1px solid var(--color-accent-orange)'
            }}>
              <p style={{ margin: 0, fontWeight: 'var(--font-weight-semibold)' }}>
                ⚠️ <strong>IMPORTANTE:</strong> Esta acción reemplazará todos los datos existentes de {marketplace} en la base de datos.
              </p>
            </div>

            <p style={{ marginBottom: 'var(--spacing-md)' }}>
              Por favor confirme la carga a la base de datos:
            </p>

            <button
              onClick={handleConfirmUpload}
              disabled={loading || uploadInProgress}
              className={`btn btn-danger ${(loading || uploadInProgress) ? 'disabled' : ''}`}
              style={{ 
                backgroundColor: 'var(--color-accent-orange)',
                borderColor: 'var(--color-accent-orange)'
              }}
            >
              {uploadInProgress ? 'Cargando...' : '📤 Cargar a la base de datos'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadToDatabase;

